"use client";

import React from "react";
import { LocaleLink as Link } from "@/components/shared/LocaleLink";
import { useTranslations } from "@/lib/i18n-client";
import { Reveal, Stagger, StaggerItem } from "@/components/shared/motion";
import { ArrowRight } from "lucide-react";

/**
 * SkyLane-native FAQ accordion — crisp <details> cards styled like the
 * HowItWorks accordion (rounded-xl border bg-card, "+" that rotates on open).
 * Claim-safe traveler FAQs + a "View all FAQ" link. No glass, no gradients.
 */
export default function FaqSection() {
  const { t } = useTranslations();

  const faqs = [
    {
      q: t("sky.faq.q1", "When should I install my eSIM?"),
      a: t("sky.faq.a1", "Install anytime before your trip while on Wi-Fi, then turn on data when you land at your destination."),
    },
    {
      q: t("sky.faq.q2", "How fast will I get my QR code?"),
      a: t("sky.faq.a2", "Your eSIM QR code is delivered by email instantly after a secure checkout."),
    },
    {
      q: t("sky.faq.q3", "Can I keep my WhatsApp number?"),
      a: t("sky.faq.a3", "Yes. The eSIM is data-only, so your home SIM and number stay active for calls, texts, and WhatsApp."),
    },
    {
      q: t("sky.faq.q4", "What network speeds can I expect?"),
      a: t("sky.faq.a4", "Plans run on reliable local 4G/5G networks, so you get fast data wherever coverage is available."),
    },
    {
      q: t("sky.faq.q5", "Will the eSIM replace my physical SIM?"),
      a: t("sky.faq.a5", "No. The eSIM runs alongside your physical SIM, so you keep both your home line and your travel data."),
    },
  ];

  return (
    <section className="container mx-auto px-4 py-12">
      <div className="max-w-3xl mx-auto">
        <Reveal as="h2" y={14} className="text-2xl sm:text-3xl font-bold text-foreground text-center mb-8">
          {t("sky.faq.title", "Frequently asked questions")}
        </Reveal>

        <Stagger as="div" staggerChildren={0.05} className="space-y-3">
          {faqs.map(({ q, a }) => (
            <StaggerItem key={q} as="div" y={10}>
              <details className="group rounded-xl border border-border bg-card p-4 shadow-sm transition-colors hover:border-primary/40">
                <summary className="flex items-center justify-between cursor-pointer font-semibold text-foreground list-none">
                  {q}
                  <span className="text-primary transition-transform duration-300 group-open:rotate-45 text-xl leading-none">+</span>
                </summary>
                <p className="mt-2 text-sm text-muted-foreground">{a}</p>
              </details>
            </StaggerItem>
          ))}
        </Stagger>

        <div className="mt-6 text-center">
          <Link
            href="/faq/"
            className="group inline-flex items-center gap-1.5 text-primary font-semibold hover:underline"
          >
            {t("sky.faq.viewAll", "View all FAQ")} <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
          </Link>
        </div>
      </div>
    </section>
  );
}
